import { FindexClient } from "@/integrations/findex";
import {
  assetShouldHaveAutomaticLogo,
  EAssetType,
  type IAsset,
  type ICompanyProfile,
  type ICryptoQuote,
  type IInvestment,
} from "findef";

// Canonical priority chain for resolving an asset/investment's logo.
//
//   1. investment.image           — user upload on this specific investment
//   2. asset.image                — IR-admin upload, or auto-resolved at create
//   3. asset.companyProfile.image
//   4. asset.companyProfile.imageSource (Clearbit URL — listed equities only)
//   5. asset.cryptoQuote.image
//   6. logo.dev/ticker/{symbol}   — listed equities only
//
// If the asset type doesn't carry an automatic logo (cash, debt, real estate,
// commodity, alternative, bank account), we short-circuit after stored uploads
// so the per-type icon fallback renders without any external lookup.
//
// `investment.userDoesNotWantImage` short-circuits everything to the icon.
export const resolveAssetImageSources = (
  asset: Partial<IAsset> | undefined | null,
  investment?: Partial<IInvestment> | null,
): string[] => {
  const out: string[] = [];

  if (investment?.userDoesNotWantImage === true) {
    return out;
  }

  if (investment?.image) {
    out.push(FindexClient.urlForAttachment(investment.image as any));
  }
  if (asset?.image) {
    out.push(FindexClient.urlForAttachment(asset.image as any));
  }

  if (!asset || !assetShouldHaveAutomaticLogo(asset as IAsset)) {
    return Array.from(new Set(out));
  }

  const cp = (typeof asset.companyProfile === "object"
    ? asset.companyProfile
    : null) as ICompanyProfile | null;
  if (cp) {
    if (cp.image) out.push(FindexClient.urlForAttachment(cp.image as any));
    if (cp.imageSource) out.push(cp.imageSource);
  }

  const cq = (typeof asset.cryptoQuote === "object"
    ? asset.cryptoQuote
    : null) as ICryptoQuote | null;
  if (cq?.image) {
    out.push(FindexClient.urlForAttachment(cq.image as any));
  }

  // Only listed equities can use the logo.dev ticker endpoint. For everything
  // else (crypto, commodities, unlisted equities) it returns letter-avatar
  // placeholders that pre-empt the per-type icon fallback.
  const isListedEquity = asset.type === EAssetType.EQUITY && asset.listed === true;
  const symbol = asset.symbol || cp?.symbol || cq?.symbol;
  if (isListedEquity && symbol) {
    out.push(FindexClient.urlForTickerLogo(symbol));
  }

  return Array.from(new Set(out));
};
